import React, { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "../AuthProvider";
import { getApiToken } from "../authUtils";
import urlconfig from "../config/urlconfig";
import { Box, Typography, Skeleton } from "@mui/material";
import { catalogTokens, parseApiError, formatMonthYear } from "./VendorCatalogTheme";

// Vertical rail of month/year buckets (newest first). Clicking one filters the
// results grid to that month; clicking it again clears the filter. Counts stay
// in sync with whatever category/price filters are active on the search page.
const VendorCatalogTimeline = ({
  vendorId, categoryName, brandName, subCategoryName, occasionId, celebrationId, minPrice, maxPrice, selected, onSelect, uploadedBy
}) => {
  const { instance } = useMsal();
  const { account } = useAuth();

  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCounts = useCallback(async () => {
    if (!account) return;
    setLoading(true);
    try {
      const token = await getApiToken(instance, account);
      const params = new URLSearchParams({
        ...(vendorId && { vendorId }),
        ...(categoryName && { categoryName }),
        ...(brandName && { brandName }),
        ...(subCategoryName && { subCategoryName }),
        ...(occasionId && { occasionId }),
        ...(celebrationId && { celebrationId }),
        ...(minPrice && { minPrice }),
        ...(maxPrice && { maxPrice }),
        ...(uploadedBy && { uploadedBy }),
      });
      const res = await fetch(`${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs/monthly-counts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await parseApiError(res));
      setCounts(await res.json());
    } catch {
      setCounts([]);
    } finally {
      setLoading(false);
    }
  }, [account, instance, vendorId, categoryName, brandName, subCategoryName, occasionId, celebrationId, minPrice, maxPrice, uploadedBy]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const isSelected = (year, month) => selected?.year === year && selected?.month === month;

  return (
    <Box
      sx={{
        width: { xs: "100%", md: 160 },
        flexShrink: 0,
        display: "flex",
        flexDirection: { xs: "row", md: "column" },
        gap: 0.75,
        overflowX: { xs: "auto", md: "visible" },
        pb: { xs: 1, md: 0 },
      }}
    >
      {loading ? (
        Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={40} sx={{ borderRadius: `${catalogTokens.radius.md}px`, minWidth: { xs: 110, md: "auto" } }} />
        ))
      ) : counts.length === 0 ? (
        <Typography variant="caption" sx={{ color: catalogTokens.color.textSecondary }}>No catalogs yet</Typography>
      ) : (
        counts.map(({ year, month, count }) => {
          const active = isSelected(year, month);
          return (
            <Box
              key={`${year}-${month}`}
              onClick={() => onSelect(active ? null : { year, month })}
              sx={{
                cursor: "pointer",
                minWidth: { xs: 110, md: "auto" },
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: `${catalogTokens.radius.md}px`,
                border: `1px solid ${active ? catalogTokens.color.primary : catalogTokens.color.border}`,
                bgcolor: active ? catalogTokens.color.primarySoft : catalogTokens.color.surface,
                transition: "all 0.15s ease",
                "&:hover": { borderColor: catalogTokens.color.primary },
              }}
            >
              <Typography variant="body2" fontWeight={active ? 700 : 600} sx={{ color: active ? catalogTokens.color.primary : catalogTokens.color.textPrimary }}>
                {formatMonthYear(year, month)}
              </Typography>
              <Typography variant="caption" sx={{ color: active ? catalogTokens.color.primary : catalogTokens.color.textSecondary, fontWeight: 700 }}>
                {count}
              </Typography>
            </Box>
          );
        })
      )}
    </Box>
  );
};

export default VendorCatalogTimeline;
